// src/pages/StaticPages.tsx
import React from 'react';
import { Link } from 'react-router-dom';
import SEO, { breadcrumbLd, SITE_URL } from '../components/common/SEO';
import Breadcrumb from '../components/common/Breadcrumb';
import Icon from '../components/common/Icon';
import InContentAd from '../components/ads/InContentAd';

const Shell: React.FC<{
    title: string;
    description: string;
    path: string;
    children: React.ReactNode;
}> = ({ title, description, path, children }) => {
    const crumbs = [
        { name: '홈', path: '/' },
        { name: title, path },
    ];
    // 본문 블록의 중간 지점에 공통 광고 1회 삽입
    const blocks = React.Children.toArray(children);
    const mid = Math.max(1, Math.floor(blocks.length / 2));
    const body = [...blocks.slice(0, mid), <InContentAd key="in-content-ad" />, ...blocks.slice(mid)];
    return (
        <>
            <SEO
                path={path}
                title={title}
                description={description}
                jsonLd={breadcrumbLd(crumbs)}
                noindex
            />
            <div className="gh-layout gh-layout--full">
                <div>
                    <Breadcrumb items={crumbs} />
                    <article className="gh-detail">
                        <header className="gh-detail__head">
                            <h1 className="gh-detail__title">{title}</h1>
                            <p className="gh-detail__summary">{description}</p>
                        </header>
                        <div className="gh-detail__body">{body}</div>
                    </article>
                </div>
            </div>
        </>
    );
};

/* ─────────────────────── 개인정보처리방침 ─────────────────────── */

export const PrivacyPage: React.FC = () => (
    <Shell
        title="개인정보처리방침"
        description="고브인포가 이용자의 정보를 어떻게 취급하는지 안내합니다."
        path="/privacy"
    >
        <section className="gh-block">
            <h2 className="gh-block__title">1. 수집하는 정보</h2>
            <p>
                고브인포(이하 &lsquo;사이트&rsquo;)는 회원가입 절차가 없으며, 이름·연락처·주민등록번호 등
                이용자를 식별할 수 있는 개인정보를 직접 수집하지 않습니다. 다만 서비스 운영과 통계 분석을
                위해 방문 기록, 브라우저 종류, 기기 정보 등이 자동으로 기록될 수 있습니다.
            </p>
        </section>

        <section className="gh-block">
            <h2 className="gh-block__title">2. 쿠키와 광고</h2>
            <p style={{ marginBottom: 12 }}>
                본 사이트는 Google AdSense를 이용해 광고를 게재합니다. Google을 포함한 제3자 광고 게재
                사업자는 쿠키를 사용하여 이용자의 이전 방문 기록을 바탕으로 광고를 게재할 수 있습니다.
            </p>
            <ul className="gh-list">
                <li>
                    <Icon name="check" size={16} strokeWidth={2.4} />
                    <span>
                        Google은 광고 쿠키를 사용해 이 사이트나 다른 사이트 방문 기록을 기반으로 광고를
                        제공합니다.
                    </span>
                </li>
                <li>
                    <Icon name="check" size={16} strokeWidth={2.4} />
                    <span>
                        이용자는{' '}
                        <a
                            href="https://www.google.com/settings/ads"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: 'var(--blue-700)', fontWeight: 600 }}
                        >
                            Google 광고 설정
                        </a>
                        에서 맞춤 광고를 해제할 수 있습니다.
                    </span>
                </li>
                <li>
                    <Icon name="check" size={16} strokeWidth={2.4} />
                    <span>
                        브라우저 설정에서 쿠키 저장을 거부할 수 있으나, 이 경우 일부 기능 이용에 제한이
                        있을 수 있습니다.
                    </span>
                </li>
            </ul>
        </section>

        <section className="gh-block">
            <h2 className="gh-block__title">3. 제휴 마케팅</h2>
            <p>
                본 사이트는 쿠팡 파트너스 활동의 일환으로, 사이트 내 상품 링크를 통해 구매가 발생하면
                이에 따른 일정액의 수수료를 제공받습니다. 이용자가 부담하는 구매 가격에는 아무런 차이가
                없으며, 해당 링크는 정부지원금 정보와 구분되도록 별도 영역에 &lsquo;쿠팡 파트너스&rsquo;
                표기와 함께 노출됩니다. 상품의 판매·배송·환불 등 거래에 관한 책임은 판매자와 쿠팡에
                있으며, 고브인포는 거래 당사자가 아닙니다.
            </p>
        </section>

        <section className="gh-block">
            <h2 className="gh-block__title">4. 정보의 제3자 제공</h2>
            <p>
                사이트는 이용자의 개인정보를 제3자에게 판매하거나 대여하지 않습니다. 법령에 따른 요청이
                있는 경우에만 관련 절차에 따라 제공될 수 있습니다.
            </p>
        </section>

        <section className="gh-block">
            <h2 className="gh-block__title">5. 외부 링크</h2>
            <p>
                본 사이트는 복지로, 정부24, 고용24 등 외부 기관 사이트로 연결되는 링크를 포함합니다.
                링크된 사이트의 개인정보 처리에 대해서는 해당 사이트의 방침이 적용되며, 고브인포는 이에
                대한 책임을 지지 않습니다.
            </p>
        </section>

        <section className="gh-block">
            <h2 className="gh-block__title">6. 문의</h2>
            <p>
                개인정보 처리에 관한 문의는 <Link to="/contact" style={{ color: 'var(--blue-700)', fontWeight: 600 }}>문의하기</Link>{' '}
                페이지를 통해 접수해 주시기 바랍니다.
            </p>
        </section>

        <div className="gh-callout">
            <Icon name="clock" size={20} />
            <div>
                <div className="gh-callout__title">시행일</div>
                <p className="gh-callout__text">본 방침은 2026년 9월 2일부터 적용됩니다.</p>
            </div>
        </div>
    </Shell>
);

/* ─────────────────────────── 이용약관 ─────────────────────────── */

export const TermsPage: React.FC = () => (
    <Shell
        title="이용약관"
        description="고브인포 서비스 이용에 관한 기본 사항을 안내합니다."
        path="/terms"
    >
        <section className="gh-block">
            <h2 className="gh-block__title">제1조 (목적)</h2>
            <p>
                본 약관은 고브인포(이하 &lsquo;사이트&rsquo;)가 제공하는 정부지원금 정보 서비스의 이용 조건과
                절차에 관한 사항을 규정함을 목적으로 합니다.
            </p>
        </section>

        <section className="gh-block">
            <h2 className="gh-block__title">제2조 (서비스의 성격)</h2>
            <p>
                사이트는 공공기관이 아닌 민간이 운영하는 정보 제공 서비스입니다. 게시된 내용은 각 부처의
                공고·보도자료 등 공개된 자료를 바탕으로 정리한 참고 정보이며, 지원금의 신청 접수나 지급을
                대행하지 않습니다.
            </p>
        </section>

        <section className="gh-block">
            <h2 className="gh-block__title">제3조 (정보의 정확성과 책임의 한계)</h2>
            <ul className="gh-list">
                <li>
                    <Icon name="check" size={16} strokeWidth={2.4} />
                    <span>
                        사이트는 정확한 정보를 제공하기 위해 노력하나, 제도 변경·예산 확정·시행령 개정 등에
                        따라 실제 내용과 차이가 발생할 수 있습니다.
                    </span>
                </li>
                <li>
                    <Icon name="check" size={16} strokeWidth={2.4} />
                    <span>
                        실제 지원 자격과 지급 금액은 개인의 소득·재산 및 소관 기관의 최종 심사 결과에 따라
                        결정됩니다.
                    </span>
                </li>
                <li>
                    <Icon name="check" size={16} strokeWidth={2.4} />
                    <span>
                        이용자는 신청 전 반드시 소관 기관의 공식 공고를 확인해야 하며, 사이트 정보에만
                        의존하여 발생한 손해에 대해 사이트는 책임을 지지 않습니다.
                    </span>
                </li>
            </ul>
        </section>

        <section className="gh-block">
            <h2 className="gh-block__title">제4조 (저작권)</h2>
            <p>
                사이트가 작성한 콘텐츠의 저작권은 고브인포에 있습니다. 무단 복제·배포를 금지하며, 인용
                시에는 출처를 명시해 주시기 바랍니다. 각 제도의 원문 정보에 대한 권리는 해당 기관에
                있습니다.
            </p>
        </section>

        <section className="gh-block">
            <h2 className="gh-block__title">제5조 (제휴 링크)</h2>
            <p>
                사이트는 쿠팡 파트너스 활동의 일환으로 상품 링크를 게재하며, 이를 통해 구매가 발생하면
                일정액의 수수료를 제공받습니다. 구매 가격에는 차이가 없습니다. 상품의 품질·배송·환불 등
                거래에 관한 책임은 판매자와 쿠팡에 있으며, 사이트는 거래의 당사자가 아닙니다.
            </p>
        </section>

        <section className="gh-block">
            <h2 className="gh-block__title">제6조 (약관의 변경)</h2>
            <p>
                사이트는 필요한 경우 본 약관을 변경할 수 있으며, 변경된 약관은 본 페이지에 게시함으로써
                효력이 발생합니다.
            </p>
        </section>
    </Shell>
);

/* ─────────────────────────── 문의하기 ─────────────────────────── */

export const ContactPage: React.FC = () => (
    <Shell
        title="문의하기"
        description="정보 정정 요청, 제휴 문의를 받고 있습니다."
        path="/contact"
    >
        <section className="gh-block">
            <h2 className="gh-block__title">정보 정정 요청</h2>
            <p>
                게시된 지원금 정보에 오류가 있거나 제도가 변경된 경우 알려주시면 확인 후 신속하게
                수정하겠습니다. 제도명과 함께 정확한 근거(공고 링크 등)를 보내주시면 반영이 빠릅니다.
            </p>
        </section>

        <section className="gh-block">
            <h2 className="gh-block__title">문의 안내</h2>
            <div className="gh-callout">
                <Icon name="document" size={20} />
                <div>
                    <div className="gh-callout__title">이메일 문의</div>
                    <p className="gh-callout__text">
                        사이트 운영·제휴·정보 정정에 관한 문의는 사이트 운영자 이메일로 보내주시기
                        바랍니다. 영업일 기준 3일 이내에 답변드립니다.
                    </p>
                </div>
            </div>
        </section>

        <section className="gh-block">
            <h2 className="gh-block__title">지원금 신청 관련 문의</h2>
            <p style={{ marginBottom: 12 }}>
                개별 지원금의 신청 자격이나 심사 결과에 대해서는 고브인포가 답변드릴 수 없습니다. 아래
                공식 상담 창구를 이용해 주세요.
            </p>
            <ul className="gh-list">
                <li>
                    <Icon name="check" size={16} strokeWidth={2.4} />
                    <span>보건복지상담센터 129 — 기초생활보장, 기초연금, 긴급복지 등 복지 전반</span>
                </li>
                <li>
                    <Icon name="check" size={16} strokeWidth={2.4} />
                    <span>고용노동부 고객상담센터 1350 — 실업급여, 육아휴직급여, 고용장려금</span>
                </li>
                <li>
                    <Icon name="check" size={16} strokeWidth={2.4} />
                    <span>국세청 국세상담센터 126 — 근로장려금, 자녀장려금</span>
                </li>
                <li>
                    <Icon name="check" size={16} strokeWidth={2.4} />
                    <span>주택도시기금 1566-9009 — 전세자금대출, 주택구입자금대출</span>
                </li>
                <li>
                    <Icon name="check" size={16} strokeWidth={2.4} />
                    <span>한국장학재단 1599-2000 — 국가장학금, 학자금대출</span>
                </li>
            </ul>
        </section>
    </Shell>
);

/* ─────────────────────────── 404 ─────────────────────────── */

export const NotFoundPage: React.FC = () => (
    <>
        <SEO
            path="/404"
            title="페이지를 찾을 수 없습니다"
            description="요청하신 페이지가 존재하지 않습니다."
            noindex
        />
        <div className="gh-layout gh-layout--full">
            <div className="gh-empty" style={{ padding: '80px 24px' }}>
                <Icon name="search" size={48} />
                <p className="gh-empty__title">페이지를 찾을 수 없습니다</p>
                <p className="gh-empty__desc" style={{ marginBottom: 20 }}>
                    주소가 변경되었거나 삭제된 페이지입니다.
                    <br />
                    찾으시는 지원금이 있다면 검색을 이용해 보세요.
                </p>
                <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
                    <Link to="/" className="gh-apply">
                        홈으로 가기
                    </Link>
                    <Link to="/search" className="gh-feat__cta" style={{ marginTop: 0 }}>
                        지원금 검색
                        <Icon name="arrowRight" size={14} strokeWidth={2.2} />
                    </Link>
                </div>
            </div>
        </div>
    </>
);

export { SITE_URL };
